import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Globe } from 'lucide-react';

import { HOME_ROUTE } from '@/constants/routes';
import { getTokens } from '@/stores/secure';
import { useAuthStore } from '@/stores/AuthState';
import { useTranslation } from '@/utils/i18n';
import { loginWithGoogle } from '@/services/googleAuth';
import { PrimaryButton } from '@/components/Buttons';
import { RacoreText, GoogleLogo, AppleLogo, EmailLogo } from '@/components/Logos';
import LightHalo from '@/components/LightHalo';
import type { LanguageType } from '@/types';

export const Route = createFileRoute('/auth/login')({
  component: LoginScreen,
});

function LoginScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userInfo = useAuthStore((s) => s.userInfo);
  const setUserInfo = useAuthStore((s) => s.setUserInfo);
  const [showLanguage, setShowLanguage] = useState(false);

  const currentLanguage = userInfo?.language || 'fr';

  // Already authenticated → skip straight to home.
  useEffect(() => {
    let cancelled = false;
    void getTokens().then(({ bearerToken }) => {
      if (!cancelled && bearerToken && userInfo.username && userInfo.email) {
        navigate({ to: HOME_ROUTE, replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, userInfo.email, userInfo.username]);

  const changeLanguage = (next: LanguageType) => {
    void setUserInfo({ ...userInfo, language: next });
    setShowLanguage(false);
  };

  return (
    <div
      className="tw:relative tw:flex tw:h-full tw:flex-col tw:overflow-hidden tw:bg-dark-primary"
      data-testid="screen-login"
    >
      {/* Ambient light effects */}
      <div className="tw:pointer-events-none tw:absolute tw:inset-0">
        <LightHalo position="top-right" size={700} intensity={40} />
        <LightHalo position="bottom-center" size={700} intensity={35} />
      </div>
      <div className="tw:absolute tw:inset-0 tw:bg-dark-primary/85" />

      {/* Language selector */}
      <div className="tw:absolute tw:left-5 tw:top-5 tw:z-10">
        <button
          type="button"
          onClick={() => setShowLanguage((v) => !v)}
          data-testid="btn-language-toggle"
          className="tw:flex tw:items-center tw:gap-1.5 tw:rounded-2xl tw:bg-dark-1/70 tw:px-2.5 tw:py-1.5 tw:text-white"
        >
          <Globe size={16} />
          <span className="tw:text-[12px] tw:font-semibold">{currentLanguage.toUpperCase()}</span>
        </button>

        {showLanguage && (
          <div className="tw:absolute tw:left-0 tw:top-10 tw:min-w-[120px] tw:rounded-xl tw:border tw:border-blue-2/30 tw:bg-dark-3/90 tw:p-2">
            {(['fr', 'en'] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => changeLanguage(lang)}
                className={
                  'tw:block tw:w-full tw:rounded-lg tw:px-3 tw:py-2 tw:text-left tw:text-[14px] tw:text-white' +
                  (currentLanguage === lang ? ' tw:bg-blue-2/30' : '')
                }
              >
                {lang === 'fr' ? 'Français' : 'English'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="tw:relative tw:z-[4] tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:px-5">
        <img
          src="/images/logo/Icon.png"
          alt=""
          aria-hidden="true"
          className="tw:mb-5 tw:size-[70px] tw:object-contain"
        />
        <RacoreText width={186} height={38} />
        <p className="tw:mb-10 tw:mt-3 tw:caption tw:italic tw:text-white/90">
          {t('login.slogan')}
        </p>

        <h1 className="tw:mb-8 tw:max-w-[320px] tw:heading-2 tw:text-center tw:text-white">
          {t('login.welcomeMessage')}
        </h1>

        <div className="tw:flex tw:w-full tw:max-w-[350px] tw:flex-col tw:items-center">
          <PrimaryButton
            label={t('login.signInWithGoogle')}
            leftIcon={<GoogleLogo width={20} height={20} />}
            onClick={loginWithGoogle}
            data-testid="btn-login-google"
          />

          <p className="tw:mb-1.5 tw:mt-12 tw:text-center tw:text-[15px] tw:font-semibold tw:text-white">
            {t('common.comingSoon')}
          </p>

          <div className="tw:flex tw:w-full tw:flex-col tw:gap-4">
            <PrimaryButton
              label={t('login.signInWithApple')}
              leftIcon={<AppleLogo width={20} height={20} />}
              disabled
              data-testid="btn-login-apple"
            />
            <PrimaryButton
              label={t('login.signInWithEmail')}
              leftIcon={<EmailLogo width={20} height={20} />}
              disabled
              data-testid="btn-login-email"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
